/**
 * policyApi.js — CEO Policy Preferences Layer API wrapper
 *
 * All endpoints use API_BASE from apiConfig.
 * Non-2xx responses throw an Error with the message from the response JSON.
 */
import { API_BASE } from '../config/apiConfig';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

async function handleResponse(res) {
  if (res.ok) return res;
  let message = `HTTP ${res.status}`;
  try {
    const body = await res.json();
    if (body && body.message) message = body.message;
  } catch (_) { /* ignore parse errors */ }
  throw new Error(message);
}

function buildUrl(path, params) {
  const url = new URL(`${API_BASE}${path}`, window.location.origin);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') {
        url.searchParams.set(k, v);
      }
    });
  }
  return url.toString();
}

// ---------------------------------------------------------------------------
// Batch upload
// ---------------------------------------------------------------------------

/**
 * Submit a batch of policy files.
 * @param {string} clientId
 * @param {Array<{category: string, file: File}>} files
 * @param {string|null} effectiveDate  ISO date string, optional
 * @returns {Promise<object>}  e.g. { batch_id, status }
 */
export async function submitBatch(clientId, files, effectiveDate) {
  const form = new FormData();
  files.forEach(({ category, file }) => {
    form.append('files', file, file.name);
    form.append('categories', category);
  });
  if (effectiveDate) {
    form.append('effective_date', effectiveDate);
  }

  const res = await fetch(
    `${API_BASE}/clients/${clientId}/policies/batch/upload`,
    { method: 'POST', body: form }
  );
  await handleResponse(res);
  return res.json();
}

// ---------------------------------------------------------------------------
// Policy queries
// ---------------------------------------------------------------------------

/**
 * Get all active policies for a client.
 * @param {string} clientId
 * @returns {Promise<object>}
 */
export async function getActivePolicies(clientId) {
  const res = await fetch(`${API_BASE}/clients/${clientId}/policies`);
  await handleResponse(res);
  return res.json();
}

/**
 * Get version history for a specific policy category.
 * @param {string} clientId
 * @param {string} category
 * @returns {Promise<object>}
 */
export async function getVersionHistory(clientId, category) {
  const res = await fetch(`${API_BASE}/clients/${clientId}/policies/${category}/versions`);
  await handleResponse(res);
  return res.json();
}

/**
 * Activate a specific version of a policy category.
 * @param {string} clientId
 * @param {string} category
 * @param {number} version
 * @returns {Promise<object>}
 */
export async function activateVersion(clientId, category, version) {
  const res = await fetch(
    `${API_BASE}/clients/${clientId}/policies/${category}/activate`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ version }),
    }
  );
  await handleResponse(res);
  return res.json();
}

/**
 * Delete a specific version of a policy category.
 * @param {string} clientId
 * @param {string} category
 * @param {number} version
 * @returns {Promise<object>}
 */
export async function deleteVersion(clientId, category, version) {
  const res = await fetch(
    `${API_BASE}/clients/${clientId}/policies/${category}/v${version}`,
    { method: 'DELETE' }
  );
  await handleResponse(res);
  return res.json();
}

// ---------------------------------------------------------------------------
// Audit log
// ---------------------------------------------------------------------------

/**
 * Get the audit log for a client.
 * @param {string} clientId
 * @param {{ category?: string, actor?: string, start?: string, end?: string, limit?: number, offset?: number }} params
 * @returns {Promise<object>}
 */
export async function getAuditLog(clientId, params = {}) {
  const url = buildUrl(`/clients/${clientId}/policies/audit`, params);
  const res = await fetch(url);
  await handleResponse(res);
  return res.json();
}

/**
 * Factory-reset the Policy Governance Layer — deactivates all active
 * self-owned governance policies and triggers CID reassembly.
 * @param {string} clientId
 * @param {string} [token]  Optional Bearer token
 * @returns {Promise<{ reset: boolean, deactivated_categories: string[], skipped_inherited: string[], message: string }>}
 */
export async function resetAllPolicies(clientId, token) {
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(
    `${API_BASE}/clients/${clientId}/policies/reset`,
    { method: 'DELETE', headers }
  );
  await handleResponse(res);
  return res.json();
}

// ---------------------------------------------------------------------------
// Consolidated view
// ---------------------------------------------------------------------------

/**
 * Get the consolidated governance document for a client.
 * @param {string} clientId
 * @returns {Promise<object>}
 */
export async function getConsolidated(clientId) {
  const res = await fetch(`${API_BASE}/clients/${clientId}/policies/consolidated`);
  await handleResponse(res);
  return res.json();
}

/**
 * Download the consolidated governance PDF as a Blob.
 * @param {string} clientId
 * @param {string} [token]  Optional Bearer token
 * @returns {Promise<Blob>}
 */
export async function downloadConsolidatedPdf(clientId, token) {
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(
    `${API_BASE}/clients/${clientId}/policies/consolidated/pdf`,
    { headers }
  );
  await handleResponse(res);
  return res.blob();
}

// ---------------------------------------------------------------------------
// Batch status & history
// ---------------------------------------------------------------------------

/**
 * Get the status of a specific batch.
 * @param {string} clientId
 * @param {string} batchId
 * @returns {Promise<object>}
 */
export async function getBatchStatus(clientId, batchId) {
  const res = await fetch(`${API_BASE}/clients/${clientId}/policies/batch/${batchId}/status`);
  await handleResponse(res);
  return res.json();
}

/**
 * Retry a failed file in a batch.
 * @param {string} clientId
 * @param {string} batchId
 * @param {string} category
 * @returns {Promise<object>}
 */
export async function retryBatchFile(clientId, batchId, category) {
  const res = await fetch(
    `${API_BASE}/clients/${clientId}/policies/batch/${batchId}/retry/${category}`,
    { method: 'POST' }
  );
  await handleResponse(res);
  return res.json();
}

/**
 * Get paginated batch history.
 * @param {string} clientId
 * @param {number} page  1-based
 * @param {number} pageSize
 * @returns {Promise<object>}
 */
export async function getBatchHistory(clientId, page = 1, pageSize = 20) {
  const url = buildUrl(`/clients/${clientId}/policies/batch/history`, { page, page_size: pageSize });
  const res = await fetch(url);
  await handleResponse(res);
  return res.json();
}

// ---------------------------------------------------------------------------
// SSE stream with auto-reconnect + poll fallback
// ---------------------------------------------------------------------------

const SSE_MAX_RETRIES = 3;
const SSE_BASE_DELAY_MS = 1000;
const POLL_INTERVAL_MS = 3000;

/**
 * Open a real-time stream for batch progress.
 *
 * Implements:
 *   - EventSource auto-reconnect with exponential backoff (max 3 retries, starting at 1 s)
 *   - Falls back to polling getBatchStatus every 3 s after 3 consecutive SSE failures
 *
 * @param {string} clientId
 * @param {string} batchId
 * @param {function} onEvent   Called with parsed event data object on each SSE message
 * @param {function} onError   Called with Error on unrecoverable error
 * @param {function} onComplete Called when batch_complete event is received
 * @returns {{ close: function }}  Call close() to stop the stream / polling
 */
export function openBatchStream(clientId, batchId, onEvent, onError, onComplete) {
  const streamUrl = `${API_BASE}/clients/${clientId}/policies/batch/${batchId}/stream`;

  let closed = false;
  let consecutiveSseFailures = 0;
  let retryCount = 0;
  let es = null;
  let pollTimer = null;
  let reconnectTimer = null;

  function close() {
    closed = true;
    if (es) {
      es.close();
      es = null;
    }
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  }

  function startPolling() {
    if (closed) return;
    pollTimer = setInterval(async () => {
      if (closed) {
        clearInterval(pollTimer);
        return;
      }
      try {
        const data = await getBatchStatus(clientId, batchId);
        onEvent(data);
        // Batch is complete when completed_at is set or all files are terminal
        const allDone = data.files && data.files.every(
          f => f.stage === 'ACTIVE' || f.stage === 'FAILED'
        );
        if (data.completed_at || allDone) {
          clearInterval(pollTimer);
          onComplete();
        }
      } catch (err) {
        onError(err);
      }
    }, POLL_INTERVAL_MS);
  }

  function connect() {
    if (closed) return;

    es = new EventSource(streamUrl);

    es.onmessage = (event) => {
      consecutiveSseFailures = 0;
      retryCount = 0;
      try {
        const data = JSON.parse(event.data);
        onEvent(data);
        if (data && data.event === 'batch_complete') {
          close();
          onComplete();
        }
      } catch (_) {
        onEvent(event.data);
      }
    };

    es.addEventListener('batch_complete', () => {
      consecutiveSseFailures = 0;
      close();
      onComplete();
    });

    es.onerror = () => {
      if (closed) return;
      es.close();
      es = null;
      consecutiveSseFailures += 1;

      if (consecutiveSseFailures >= SSE_MAX_RETRIES) {
        // Fall back to polling
        onError(new Error('SSE connection failed after 3 retries; switching to polling'));
        startPolling();
        return;
      }

      // Exponential backoff reconnect
      const delay = SSE_BASE_DELAY_MS * Math.pow(2, retryCount);
      retryCount += 1;
      reconnectTimer = setTimeout(connect, delay);
    };
  }

  connect();

  return { close };
}
